import {
  formatNamedDayTime,
  tt,
  zonedDateParam,
  zonedWallTime,
  zonedWallTimeToUtcMs,
} from '@agos/shared'

/**
 * Taking an order for later, at the till. The rules behind legacy's `CasenkaToggle`.
 *
 * **A missed port, not a new feature** — legacy's `new-order.tsx:1885` renders the control and the AGOS
 * builder had nothing (FB-193). What is here is legacy's arithmetic, with its one bug fixed.
 *
 * The conversion is `@agos/shared`'s two-pass `zonedWallTimeToUtcMs`. Legacy's `bratislavaDateTimeMs`
 * samples the zone offset at the naive time *treated as UTC* and applies it once — FB-C09, measured on
 * the customer side, where 01:30 on 29 March 2026 becomes `2026-03-28T23:30Z`. Every other instant is
 * identical, so this is a fix rather than a change in behaviour.
 */

/** What goes in `scheduledAt` — an instant the server will parse, or `null` when a piece is missing. */
export function scheduledAtOf(date: string, time: string): string | null {
  if (date === '' || time === '') return null
  const instant = zonedWallTimeToUtcMs(date, time)
  return Number.isNaN(instant) ? null : new Date(instant).toISOString()
}

/**
 * The instant a saved order was scheduled for, back as the two fields that produced it.
 *
 * Editing a pre-order has to reopen on its own time — legacy `new-order.tsx:573`. Without this, opening
 * a časenka to add a drink and saving would send the order back with its time blank.
 */
export function partsOf(scheduledAt: string): { date: string; time: string } {
  const instant = Date.parse(scheduledAt)
  if (Number.isNaN(instant)) return { date: '', time: '' }
  const wall = zonedWallTime(instant)
  const pad = (value: number) => String(value).padStart(2, '0')
  return {
    date: zonedDateParam(instant),
    time: `${pad(Math.floor(wall.minutes / 60))}:${pad(wall.minutes % 60)}`,
  }
}

/** Today in the restaurant's zone — the earliest date the picker offers. Legacy's `minDate`. */
export function todayInZone(now: Date): string {
  return zonedDateParam(now.getTime())
}

/**
 * The earliest time the picker offers, which is a restriction **only on today**. Legacy's `minTime`.
 *
 * Ten minutes out, because a časenka for two minutes' time is an ordinary order typed the long way and
 * the kitchen board would show it as already due. On any other date every time is offered — including
 * one earlier in the day than now, which is the whole point of scheduling.
 *
 * **After 23:50 there is no such time.** Ten minutes past 23:50 is tomorrow, and the wall clock wraps
 * to `00:00` — a time that has already passed today, and indistinguishable from the `'00:00'` this
 * returns for *no restriction*. So the last ten minutes of the day silently offered the whole of it.
 * Found by an e2e that happened to run at 23:50; it is now pinned rather than left to chance.
 *
 * The honest answer is that today is full: {@link NO_TIME_LEFT_TODAY} is a `min` no time can satisfy,
 * so the picker refuses every one of them and {@link noTimeLeftToday} lets a screen say why.
 */
export const NO_TIME_LEFT_TODAY = '23:59:59'

export function earliestTime(date: string, now: Date): string {
  if (date !== todayInZone(now)) return '00:00'
  const wall = zonedWallTime(now.getTime() + 10 * 60_000)
  // Wrapped past midnight: the ten minutes ran out of today.
  if (wall.minutes < zonedWallTime(now.getTime()).minutes) {
    return NO_TIME_LEFT_TODAY
  }
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${pad(Math.floor(wall.minutes / 60))}:${pad(wall.minutes % 60)}`
}

/** Whether the ten-minute rule has run today out of bookable times. */
export function noTimeLeftToday(date: string, now: Date): boolean {
  return earliestTime(date, now) === NO_TIME_LEFT_TODAY
}

/** Said beside the picker, because a field that refuses everything with no reason reads as broken. */
export function noTimeLeftMessage(): string {
  return tt('Dnes už časenku zadať nemožno - objednávka musí byť aspoň o desať minút. Vyberte zajtrajší dátum.')
}

/**
 * `piatok 24. 12. 18:00` — the scheduled time echoed back, or `null` while a piece is missing.
 *
 * Legacy's `toLocaleString('sk-SK', …)` on the **converted** instant, so what the till shows is what was
 * understood rather than what was typed: the day name is derived, which is how *sobota* entered for
 * *nedeľa* is caught before the order exists.
 */
export function scheduledLabelOf(date: string, time: string): string | null {
  const iso = scheduledAtOf(date, time)
  return iso === null ? null : formatNamedDayTime(iso)
}

/**
 * Whether the lunch menu on screen is a different day from the one being delivered — legacy's
 * `lunchMismatch`, which **blocks the order** there.
 *
 * A lunch line is named and priced from the day it was authored for. Ordering Wednesday's *bryndzové
 * halušky* for delivery on Thursday sends the kitchen a dish that is not on Thursday's menu, so legacy
 * disables the button rather than warning. Kept, because the alternative is an order nobody can cook.
 */
export function lunchMismatch(
  isPreorder: boolean,
  scheduledDate: string,
  lunchDate: string,
): boolean {
  return isPreorder && scheduledDate !== '' && scheduledDate !== lunchDate
}
